import React, { memo, useMemo, useEffect } from 'react';
import cn from 'classnames';
import useSWRInfinite from 'swr/infinite';
import queryString from 'query-string';
import { useRecoilValue } from 'recoil';

import { useRouter } from 'next/router';
import { Icon, Spinner } from 'components';
import { Row } from 'components/layout';
import EventCard from 'components/ComponentV2/EventCard';
import { Button } from 'components/form';
import { ButtonV3 } from 'components/ComponentV2';
import { httpHeadersState } from 'lib/atoms/userSecretAtom';
import { get } from 'lib/utils/http';
import { API_ROUTES } from 'lib/api-routes';
import {
  parsePaginatedResponse,
  parseResponse,
  parseArrayResponse,
} from 'lib/utils/parser';
import { PAGE_ROUTES } from 'lib/page-routes';
import {
  OpportunityAttributes,
  OpportunitiesResponse,
} from 'lib/models/opportunity';
import { EventTouchpointable } from 'lib/models/event';
import { getOpportunityPublicPagePathname } from 'utils/urls';
import styles from '../Section.module.scss';

const PER_PAGE_COUNT = 3;

export interface EventRecoProps {
  /** Custom class name */
  className?: string;
  /** Custom style */
  style?: Record<string, unknown>;
}

export const EventReco: React.FC<EventRecoProps> = memo(
  ({
    className = '', // custom class name
    style, // custom style
  }: EventRecoProps) => {
    const router = useRouter();
    const { headers } = useRecoilValue(httpHeadersState);

    const endOfPageRef = React.useRef<HTMLDivElement | null>(null);
    const scrollToBottom = () => {
      endOfPageRef.current?.scrollIntoView({ behavior: 'smooth' });
    };

    const getTouchpointListKey = (pageIndex: number) => {
      const params = {
        page: pageIndex + 1,
        per_page: PER_PAGE_COUNT,
        with_details: true,
      };
      const qs = queryString.stringify(params, {
        arrayFormat: 'bracket',
        skipEmptyString: true,
      });
      const pathname = `${API_ROUTES.CANDIDATE_TOUCHPOINT}${API_ROUTES.EVENT_RECOMMENDATIONS}?${qs}`;

      return [pathname, headers];
    };

    const {
      data: touchpointsResponse,
      error: touchpointsError,
      size,
      setSize,
    } = useSWRInfinite<OpportunitiesResponse>(getTouchpointListKey, get, {
      revalidateOnFocus: false,
      revalidateFirstPage: false,
    });

    const isAPILoading = !touchpointsResponse && !touchpointsError;
    const isLoadingMore =
      isAPILoading ||
      (size > 0 &&
        touchpointsResponse &&
        typeof touchpointsResponse[size - 1] === 'undefined');

    const touchpoints = useMemo(() => {
      return parsePaginatedResponse<OpportunityAttributes>(touchpointsResponse);
    }, [touchpointsResponse]);

    const totalCount = touchpointsResponse
      ? touchpointsResponse[0]?.meta?.total
      : undefined;

    const hasMoreRecords = useMemo(() => {
      const firstApplicationList = Array.isArray(touchpointsResponse)
        ? touchpointsResponse[0]
        : null;
      if (firstApplicationList) {
        return (firstApplicationList?.meta?.total || 0) > size * PER_PAGE_COUNT;
      } else {
        return false;
      }
    }, [touchpointsResponse, size]);

    useEffect(() => {
      if (!isLoadingMore && size > 1) {
        scrollToBottom();
      }
    }, [isLoadingMore, size]);

    useEffect(() => {
      setSize(1);

      return () => {
        endOfPageRef.current = null;
        setSize(1);
      };
    }, []);

    const getTouchpointCard = (touchpoints: Array<OpportunityAttributes>) => {
      return (
        <div className={styles.touchpointcardList}>
          {touchpoints?.map(
            ({
              id,
              title,
              slug,
              touchpointable_type,
              start_date,
              avatar_urls,
              applications,
              touchpointable: touchpointableResponse,
              application_deadline,
              cities,
              creatable_for,
              cohost_applications: cohostApplicationsResponse,
              show_guest_list,
            }) => {
              const url = getOpportunityPublicPagePathname(
                touchpointable_type,
                slug
              );
              const touchpointable = parseResponse(
                touchpointableResponse as EventTouchpointable
              );
              const cohost_applications = parseArrayResponse(
                cohostApplicationsResponse
              );

              return (
                <EventCard
                  key={id}
                  title={title}
                  url={url}
                  start_date={start_date}
                  avatar_urls={avatar_urls}
                  applications={applications}
                  icon_url={touchpointable?.icon_url}
                  registrationsDesc={
                    ' students already registered to this event.'
                  }
                  application_deadline={application_deadline}
                  cities={cities}
                  creatable_for={creatable_for}
                  cohost_applications={cohost_applications}
                  showDetails={true}
                  className={styles.touchpointcard}
                  show_guest_list={show_guest_list}
                />
              );
            }
          )}
        </div>
      );
    };

    const contentClassNames = cn(
      {
        [styles.container]: true,
      },
      styles.section,
      className
    );

    return (
      <div className={contentClassNames} style={style} data-testid="EventReco">
        <p className={styles.title}>Events for you</p>
        <div ref={endOfPageRef}></div>

        {totalCount ? getTouchpointCard(touchpoints) : null}
        {totalCount === undefined || isLoadingMore ? (
          <Spinner
            size="small"
            className={cn(styles.spinner)}
            wrapper={totalCount === undefined ? true : false}
            wrapperClassname={cn({
              [styles['min-height']]: totalCount === undefined,
            })}
          />
        ) : null}
        {totalCount ? (
          <Row align="center" columnGap={8} className={styles.countsRow}>
            {touchpoints?.length ? (
              <p>
                Showing {touchpoints?.length} of {totalCount}
              </p>
            ) : null}
            {hasMoreRecords ? (
              <Button
                variant="text"
                color="blue"
                onClick={() => {
                  setSize(size + 1);
                }}
                className={styles.countsButton}
              >
                Show more
              </Button>
            ) : null}
          </Row>
        ) : null}
        {totalCount === 0 ? (
          <Row columnGap={10} wrap={false} className={styles.nodataSection}>
            <p className={styles.emoji}>
              <Icon
                className={styles.icon}
                iconName="icon_calendar-2"
                size="auto"
              />
            </p>
            <div>
              <p className={styles.text}>
                There aren&apos;t currently any events at your university or
                near you. Follow more companies and join communities so you
                don&apos;t miss out on any events.
              </p>
              <Row columnGap={10}>
                <ButtonV3
                  color="blue"
                  variant="text"
                  onClick={() => router.push(PAGE_ROUTES.SOCIETIES_SEARCH)}
                  className={styles.bluebutton}
                >
                  Explore communities
                </ButtonV3>
                <ButtonV3
                  color="blue"
                  variant="text"
                  onClick={() => router.push(PAGE_ROUTES.COMPANIES_SEARCH)}
                  className={styles.bluebutton}
                >
                  Explore companies
                </ButtonV3>
              </Row>
            </div>
          </Row>
        ) : null}
      </div>
    );
  }
);

EventReco.displayName = 'EventReco';
